import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  CardContent,
  CardActionArea,
  Paper,
} from '@mui/material';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DescriptionIcon from '@mui/icons-material/Description';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const insightsSections = [
  {
    title: 'Analytics',
    description: 'Explore interactive charts and detailed metrics analysis',
    icon: AnalyticsIcon,
    path: '/samples/insights/analytics',
    color: '#008080',
  },
  {
    title: 'Reports',
    description: 'View and download pre-built business reports',
    icon: DescriptionIcon,
    path: '/samples/insights/reports',
    color: '#F4A261',
  },
  {
    title: 'Dashboards',
    description: 'Browse dashboard templates for quick insights',
    icon: DashboardIcon,
    path: '/samples/insights/dashboards',
    color: '#003366',
  },
];

const latestInsights = [
  {
    type: 'opportunity',
    icon: TrendingUpIcon,
    title: 'Revenue Growth Opportunity',
    description: 'Your Premium Widget Pro has shown 24% growth this week. Consider increasing marketing spend in this category.',
    impact: 'High',
  },
  {
    type: 'warning',
    icon: WarningAmberIcon,
    title: 'Inventory Alert',
    description: 'Widget Starter Pack inventory is running low. Based on current sales velocity, you have approximately 12 days of stock remaining.',
    impact: 'Medium',
  },
  {
    type: 'success',
    icon: CheckCircleIcon,
    title: 'Customer Retention Improved',
    description: 'Your customer retention rate has improved by 8% compared to last quarter. Keep up the excellent support quality.',
    impact: 'Positive',
  },
  {
    type: 'insight',
    icon: LightbulbIcon,
    title: 'Regional Expansion Suggestion',
    description: 'Based on shipping data analysis, there is untapped demand in the West region. Consider targeted marketing campaigns.',
    impact: 'Strategic',
  },
];

const SampleInsights: React.FC = () => {
  const navigate = useNavigate();

  const getInsightColors = (type: string) => {
    switch (type) {
      case 'opportunity':
        return { bg: 'rgba(46, 125, 50, 0.08)', border: 'success.main', iconColor: 'success.main' };
      case 'warning':
        return { bg: 'rgba(237, 108, 2, 0.08)', border: 'warning.main', iconColor: 'warning.main' };
      case 'success':
        return { bg: 'rgba(46, 125, 50, 0.08)', border: 'success.main', iconColor: 'success.main' };
      case 'insight':
        return { bg: 'rgba(2, 136, 209, 0.08)', border: 'info.main', iconColor: 'info.main' };
      default:
        return { bg: 'grey.50', border: 'grey.300', iconColor: 'grey.500' };
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
        Insights
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 4 }}>
        AI-powered analysis and recommendations for your business
      </Typography>

      {/* Quick Navigation Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {insightsSections.map((section) => {
          const IconComponent = section.icon;
          return (
            <Grid item xs={12} md={4} key={section.title}>
              <Card
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  height: '100%',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: section.color,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(section.path)}
                  sx={{ height: '100%', p: 3 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        bgcolor: `${section.color}15`,
                        p: 1.5,
                        borderRadius: 2,
                      }}
                    >
                      <IconComponent sx={{ color: section.color, fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 600, mb: 0.5 }}>
                        {section.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {section.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Latest AI Insights */}
      <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <LightbulbIcon sx={{ color: 'accent.main' }} />
          <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 700 }}>
            Latest AI Insights
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {latestInsights.map((insight, index) => {
            const colors = getInsightColors(insight.type);
            const IconComponent = insight.icon;
            return (
              <Grid item xs={12} md={6} key={index}>
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: colors.bg,
                    borderRadius: 2,
                    borderLeft: 4,
                    borderColor: colors.border,
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <IconComponent sx={{ color: colors.iconColor, fontSize: 24, mt: 0.25 }} />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                          {insight.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            bgcolor: colors.border,
                            color: 'white',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontWeight: 600,
                          }}
                        >
                          {insight.impact}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {insight.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
};

export default SampleInsights;
